import React, {useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {SESSION_KEY} from '../auth/session';
import {RSS_API_URL} from '../constants';

type LoginResponse = {
  id?: number;
  access_token?: string;
  refresh_token?: string;
  expire_in?: number;
  token_type?: string;
};

/**
 * Sign-in page. Wire `REACT_APP_AUTH_LOGIN_URL` to your API (POST JSON `{ email, password }`, expect 200).
 * Without it, sign-in accepts any email with `@` and password length ≥ 8 (local demo only).
 */
const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    setError(null);
    const authUrl = `${RSS_API_URL}/rss/login_with_email`;

    if (!email.trim() || !password) {
      setError('Enter email and password.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Enter a valid email address.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setBusy(true);
    try {
      const res = await fetch(authUrl, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({email: email.trim(), password}),
      });
      if (!res.ok) {
        setError('Sign-in failed. Check your credentials.');
        return;
      }
      let data: LoginResponse = (await res.json()) as LoginResponse;
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify(data)
      );
      navigate('/posts', {replace: true});
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-shell">
        <Link to="/" className="login-back">
          ← Back to CriticalMinerals.News
        </Link>
        <div className="login-card">
          <h1 className="login-title">Sign in</h1>
          <p className="login-lead">
            Use your account to access saved preferences and future subscriber features.
          </p>
          <form className="login-form" onSubmit={submit} noValidate>
            <label className="login-label">
              Email
              <input
                className="login-input"
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
              />
            </label>
            <label className="login-label">
              Password
              <input
                className="login-input"
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
              />
            </label>
            {error ? <p className="login-error">{error}</p> : null}
            <button type="submit" className="login-submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
